type ConnectedPlatform = 'tiktok' | 'instagram' | 'youtube' | 'unknown';

const PLATFORM_ORDER: ConnectedPlatform[] = ['tiktok', 'instagram', 'youtube'];

const PLATFORM_PATTERNS: Record<Exclude<ConnectedPlatform, 'unknown'>, RegExp[]> = {
  tiktok: [/tiktok\.com/i],
  instagram: [/instagram\.com/i, /instagr\.am/i],
  youtube: [/youtube\.com/i, /youtu\.be/i],
};

function hasMeaningfulString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function addPlatform(set: Set<ConnectedPlatform>, platform: ConnectedPlatform) {
  if (platform === 'unknown') return;
  set.add(platform);
}

function matchPlatformFromString(value: string): ConnectedPlatform | null {
  for (const platform of PLATFORM_ORDER) {
    const patterns = PLATFORM_PATTERNS[platform as Exclude<ConnectedPlatform, 'unknown'>];
    if (patterns?.some((pattern) => pattern.test(value))) {
      return platform;
    }
  }
  return null;
}

function addFromString(set: Set<ConnectedPlatform>, value: unknown) {
  if (!hasMeaningfulString(value)) return;
  const platform = matchPlatformFromString(value.toLowerCase());
  if (platform) {
    addPlatform(set, platform);
  }
}

function addFromPlatformKey(set: Set<ConnectedPlatform>, value: unknown) {
  if (!hasMeaningfulString(value)) return;
  const normalized = value.toLowerCase();
  if (normalized.includes('tiktok')) addPlatform(set, 'tiktok');
  if (normalized.includes('instagram') || normalized.includes('instagr')) addPlatform(set, 'instagram');
  if (normalized.includes('youtube') || normalized.includes('youtu')) addPlatform(set, 'youtube');
}

function addFromPlatformArray(set: Set<ConnectedPlatform>, value: unknown) {
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      if (typeof entry === 'string') {
        addFromPlatformKey(set, entry);
      } else if (entry && typeof entry === 'object') {
        addFromPlatformKey(set, (entry as any).platformName);
        addFromPlatformKey(set, (entry as any).platform);
        addFromString(set, (entry as any).url);
        addFromString(set, (entry as any).link);
      }
    });
  } else {
    addFromPlatformKey(set, value);
  }
}

function addFromSocialLinks(set: Set<ConnectedPlatform>, value: unknown) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      addFromString(set, (entry as any).url);
      addFromPlatformKey(set, (entry as any).label);
    });
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      addFromPlatformKey(set, key);
      addFromString(set, entryValue);
      if (entryValue && typeof entryValue === 'object') {
        addFromString(set, (entryValue as any).url);
        addFromPlatformKey(set, (entryValue as any).label);
      }
    });
  }
}

function addFromTopLevelKeys(set: Set<ConnectedPlatform>, profile: Record<string, unknown>) {
  Object.entries(profile).forEach(([key, value]) => {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('tiktok')) addFromString(set, value);
    if (keyLower.includes('instagram')) addFromString(set, value);
    if (keyLower.includes('youtube') || keyLower.includes('youtu')) addFromString(set, value);
  });
}

export function getConnectedPlatforms(profile: unknown): ConnectedPlatform[] {
  if (!profile || typeof profile !== 'object') {
    return [];
  }

  const data = profile as Record<string, unknown>;
  const connected = new Set<ConnectedPlatform>();

  // Identity primary platforms (string or array)
  addFromPlatformArray(connected, (data.identity as any)?.primaryPlatforms);
  addFromPlatformArray(connected, (data.identity as any)?.primaryPlatform);

  // Structured platform arrays (e.g., profile.platforms)
  addFromPlatformArray(connected, data.platforms);

  // Platform stats keys (e.g., platformStats.tiktok)
  if (data.platformStats && typeof data.platformStats === 'object') {
    Object.keys(data.platformStats).forEach((platformKey) => addFromPlatformKey(connected, platformKey));
  }

  // Social links (object/array)
  addFromSocialLinks(connected, data.socialLinks);

  // Generic links array
  if (Array.isArray(data.links)) {
    data.links.forEach((entry) => addFromString(connected, (entry as any)?.url));
  }

  // Top-level fallbacks (tiktokUsername, instagramHandle, youtubeChannelUrl, etc.)
  addFromTopLevelKeys(connected, data);

  return PLATFORM_ORDER.filter((platform) => connected.has(platform));
}
